import {
  AdminSpinner,
  Button,
  Div,
  H3,
  H5,
  LocationPinIcon,
  Modal,
  ModalFullScreen,
  P,
  PropsModal,
  Separator,
  getLocationsByUserId,
  getUserById,
  renderRole,
  useAuthenticatedAuthUser,
  useUsers,
  reportError,
} from '@vgrubs/components';
import {
  Owner,
  SimpleLocationEntity,
} from 'components/src/types/location.types';
import {
  SimpleUserEntity,
  User,
  UserRole,
} from 'components/src/types/user.types';
import { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { KeyValueInfo } from '../Verifications/components/KeyValueInfo';
import { UserCreditCard } from './Bank/UserCreditCard';
import { UserCreditCardAdd } from './Bank/UserCreditCardAdd';

export const UsersAndAccessDetailsModal = ({
  modalIsOpen,
  closeModal,
  handleOnDelete,
  handleOnEdit,
  noActions,
  callGetUser,
}: PropsModal & {
  modalIsOpen: User | SimpleUserEntity | null | Owner;
  handleOnDelete?: (user: User) => void;
  handleOnEdit?: (user: User) => void;
  noActions?: boolean;
  callGetUser?: boolean;
}) => {
  const [user, setUser] = useState<User | null>(null);
  const [locations, setLocations] = useState<SimpleLocationEntity[]>([]);
  const [loading, setLoading] = useState(false);

  const { loading: loadingBank } = useUsers((state) => state);

  const { role } = useAuthenticatedAuthUser();

  const hasLocations =
    user?.role !== UserRole.admin && user?.role !== UserRole.support;

  const hasBank = user?.role === UserRole.partner && role === UserRole.admin;


  async function getUserLocations() {
    if (!modalIsOpen) return;
    try {
      setLoading(true);
      const { data } = await getLocationsByUserId(modalIsOpen.id);
      setLocations(data.data);
      setLoading(false);
    } catch (error) {
      reportError(error);
      setLoading(false);
    }
  }

  async function getUserData() {
    if (!modalIsOpen) return;

    try {
      setLoading(true);
      const { data } = await getUserById(modalIsOpen.id);
      setUser(data.data);
      setLoading(false);
    } catch (error) {
      reportError(error);
      setLoading(false);
    }
  }

  useEffect(() => {
    if (modalIsOpen?.id) {
      setUser(modalIsOpen as User);

      if (callGetUser) {
        getUserData();
      }
      if (hasLocations) {
        getUserLocations();
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [modalIsOpen]);

  //TODO REFACTOR MODALS

  if (!user) {
    return null;
  }

  return (
    <>
      {(loading || loadingBank) && <AdminSpinner />}
      {hasBank ? (
        <ModalFullScreen
          isOpen={!!modalIsOpen}
          closeModal={() => {
            closeModal?.();
          }}
        >
          <Div $flex $column>
            <H3 $mb="35">
              <span>{user?.firstName + ' ' + user?.lastName}</span>
            </H3>
            <Div
              $flex
              $alignCenter
              $width="100%"
              $gap="50px"
              $gapSM="32px"
              $columnSM
              $mb="32px"
            >
              <Div $width="100%">
                <Separator />

                {user?.email && <KeyValueInfo k="Email" v={user?.email} />}

                {user?.phoneNumber && (
                  <KeyValueInfo k="Phone" v={user?.phoneNumber} />
                )}

                <KeyValueInfo k="Role" v={renderRole(user?.role)} />
              </Div>
              {hasBank && (
                <Div $widthSM="100%" $flex $column $center $gap="32px">
                  <UserCreditCard id={user?.id} locations={locations} />
                </Div>
              )}
            </Div>

            {hasLocations && (
              <>
                <H5 $mt="20" $mb="20">
                  Locations
                </H5>
                {locations.map(({ name, id }, i) => {
                  return (
                    <Link
                      to={`/locations/${id}`}
                      style={{ width: '100%' }}
                      key={i}
                    >
                      <Div
                        $box
                        $p="12px 20px"
                        $mb="8"
                        $flex
                        $between
                        $alignCenter
                      >
                        <H5 $m="0">{name}</H5>
                        <LocationPinIcon />
                      </Div>
                    </Link>
                  );
                })}
                {locations.length < 1 && <P>No Locations</P>}
              </>
            )}

            {!noActions && (
              <Div $flex $columnSM $gap="32px" $gapSM="16px" $mt="32">
                <Div $width="33%" $widthSM="100%">
                  <Link to={`/transactions/${user?.id}`} state={user}>
                    <Button $selected $w="100%">
                      View transactions
                    </Button>
                  </Link>
                </Div>
                <Div $width="33%" $widthSM="100%">
                  <Button
                    $selected
                    $gray
                    $w="100%"
                    onClick={() => handleOnEdit?.(user)}
                  >
                    Edit User
                  </Button>
                </Div>

                <Div $width="33%" $widthSM="100%">
                  <Button
                    $selected
                    $w="100%"
                    onClick={() => handleOnDelete?.(user)}
                  >
                    Delete User
                  </Button>
                </Div>
              </Div>
            )}
          </Div>
        </ModalFullScreen>
      ) : (
        <Modal
          isOpen={!!modalIsOpen}
          logo={false}
          header="User Details"
          closeModal={closeModal}
        >
          <Div $flex $column>
            <H3 $mb="35">
              <span>{user?.firstName + ' ' + user?.lastName}</span>
            </H3>
            <Div
              $flex
              $alignCenter
              $width="100%"
              $gap="50px"
              $gapSM="32px"
              $columnSM
              $mb="32px"
            >
              <Div $width="100%">
                <Separator />

                {user?.email && <KeyValueInfo k="Email" v={user?.email} />}

                {user?.phoneNumber && (
                  <KeyValueInfo k="Phone" v={user?.phoneNumber} />
                )}

                <KeyValueInfo k="Role" v={renderRole(user?.role)} />
              </Div>
              {hasBank && (
                <Div $widthSM="100%" $flex $center>
                  <UserCreditCard
                    id={user?.id as number}
                    locations={locations}
                  />
                </Div>
              )}
            </Div>

            {hasLocations && (
              <>
                <H5 $mt="20" $mb="20">
                  Locations
                </H5>
                {locations.map(({ name, id }, i) => {
                  return (
                    <Link
                      to={`/locations/${id}`}
                      style={{ width: '100%' }}
                      key={i}
                    >
                      <Div
                        $box
                        $p="12px 20px"
                        $mb="8"
                        $flex
                        $between
                        $alignCenter
                      >
                        <H5 $m="0">{name}</H5>
                        <LocationPinIcon />
                      </Div>
                    </Link>
                  );
                })}
                {locations.length < 1 && <P>No Locations</P>}
              </>
            )}

            {!noActions && (
              <Div $flex $column $gap="16px" $mt="32">
                <Div $width="100%" $widthSM="100%">
                  <Link to={`/transactions/${user?.id}`} state={user}>
                    <Button $selected $w="100%">
                      View transactions
                    </Button>
                  </Link>
                </Div>
                <Div $width="100%">
                  <Button
                    $selected
                    $gray
                    $w="100%"
                    onClick={() => handleOnEdit?.(user)}
                  >
                    Edit User
                  </Button>
                </Div>
                <Div $width="100%">
                  <Button
                    $selected
                    $w="100%"
                    onClick={() => handleOnDelete?.(user)}
                  >
                    Delete User
                  </Button>
                </Div>
              </Div>
            )}
          </Div>
        </Modal>
      )}
      <UserCreditCardAdd id={user?.id} />
      {/* <UserCreditCardDelete id={user?.id} /> */}
      {/* <UserCreditCardEditBalance id={user?.id} /> */}
    </>
  );
};
